/**
 * Component: Localized Time
 * -----------------------------------------------------------------------------
 * Custom Element for date & time converting.
 * - This converts and shows date & time for customers in their timezone.
 *
 * @namespace LocalizedTime
 */

import { kebabToCamel } from '~/helpers/strings'
import { parseAttributeValue } from '~/helpers/dom'
import { strftime } from '~/helpers/format'

const customElementName = 'localized-time'

/**
 * Create LocalizedTime Class.
 */
export default class LocalizedTime extends window.HTMLElement {

  /**
   * Returns an array of attribute names to observe for changes.
   *
   * @return {Array<string>} An array of attribute names.
   */
  static get observedAttributes() {
    return ['state']
  }

  /**
   * Initialise.
   */
  constructor() {
    super()

    this.setDefaultState()
  }

  /**
   * Invokes each time the custom element is appended into a document-connected
   * element
   * - this will happen each time the node is moved and may happen before
   * the element's contents have been fully parsed.
   */
  connectedCallback() {
    this.convertToLocalTime()
  }

  /**
   * Callback for attribute changes.
   * @param {string} name - The name of the changed attribute.
   * @param {string} oldValue - The previous value of the attribute.
   * @param {string} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    this.handleAttributeChange(name, newValue)
  }

  /**
   * Set the default state.
   */
  setDefaultState() {

    /* Observed data */
    this.state = parseAttributeValue(this.getAttribute('state'))

    this.timezone = parseAttributeValue(this.getAttribute('js-timezone')) // Either BST or GMT
    this.date = parseAttributeValue(this.getAttribute('js-date'))
    this.dateFormat = parseAttributeValue(this.getAttribute('js-date-format'))
  }

  /**
   * Convert the date & time to the customer's local timezone.
   */
  convertToLocalTime() {
    this.setAttribute('state', 'loading')

    const customerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const timezoneAbbreviation = this.getTimezoneAbbreviation(customerTimezone)

    // Create a new Date object representing the time in the customer's timezone
    const dateInCustomerTimezone = new Date(
      this.date.toLocaleString('en-US', {
        timeZone: customerTimezone,
      }),
    )

    const formattedOutput = strftime(
      dateInCustomerTimezone,
      this.dateFormat.replace('%Z', timezoneAbbreviation),
    )

    this.textContent = formattedOutput

    this.setAttribute('state', 'ready')
  }

  /**
   * Update the state based on attribute changes.
   * @param {string} name - The name of the changed attribute.
   * @param {string} value - The new value of the attribute.
   */
  handleAttributeChange(name, value) {
    const attributeName = kebabToCamel(name)

    // Parse and format the attribute value
    const formattedValue = parseAttributeValue(value)

    // Update the state
    this[attributeName] = formattedValue
  }

  /**
   * Get the timezone abbreviation based on its name.
   * @param {String} timezoneName - The timezone name.
   * @returns {String|Boolean} - The timezone abbreviation or `false`.
   */
  getTimezoneAbbreviation(timezoneName) {
    if (!timezoneName) {
      return false
    }

    try {
      return new Intl.DateTimeFormat('en', {
        timeZone: timezoneName,
        timeZoneName: 'short',
      })
        .formatToParts()
        .find((part) => part.type === 'timeZoneName').value
    } catch (error) {
      mint.Log('warning', 'Unable to get timezone abbreviation', error, {
        timezone: timezoneName,
      })

      return false
    }
  }
}

/**
 * Register element.
 */
if ('customElements' in window && !customElements.get(customElementName)) {
  customElements.define(customElementName, LocalizedTime)
}
